package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Featured business card for the Home carousel (Stitch fac255ec). Image area with a rating badge
 * overlay, then name / subtitle / location. [image] slot draws the photo (placeholder until wired).
 */
@Composable
fun FeaturedBusinessCard(
    name: String,
    subtitle: String,
    rating: Float,
    city: String,
    modifier: Modifier = Modifier,
    /** Localized "New" label; when set it replaces the star-rating badge (zero-review listings). */
    newLabel: String? = null,
    onClick: (() -> Unit)? = null,
    coverIcon: String = "storefront",
    coverImageRes: org.jetbrains.compose.resources.DrawableResource? = null,
    sponsoredLabel: String? = null,
    image: @Composable (() -> Unit)? = null,
) {
    AppCard(modifier = modifier.width(AppTheme.sizes.featuredCardWidth), onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 10f)
                    .clip(AppTheme.shapes.card),
                contentAlignment = Alignment.TopEnd,
            ) {
                if (image != null) image() else BusinessCover(iconName = coverIcon, placeholderImageRes = coverImageRes)
                // Sponsored badge (top-start), so paid placements are always clearly labeled
                if (sponsoredLabel != null) {
                    SponsoredBadge(
                        label = sponsoredLabel,
                        modifier = Modifier.align(Alignment.TopStart).padding(AppTheme.spacing.sm),
                    )
                }
                // Rating badge (or the caller-localized "New" label for zero-review listings).
                Row(
                    modifier = Modifier
                        .padding(AppTheme.spacing.sm)
                        .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                        .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xxs),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                ) {
                    if (newLabel != null) {
                        Text(
                            text = newLabel,
                            style = AppTheme.typography.labelSm,
                            color = AppTheme.colors.secondary,
                        )
                    } else {
                        AppIcon(name = "star", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.secondary, filled = true)
                        Text(
                            text = ratingLabel(rating),
                            style = AppTheme.typography.labelSm,
                            color = AppTheme.colors.onSurface,
                        )
                    }
                }
            }
            Column(modifier = Modifier.padding(AppTheme.spacing.lg)) {
                Text(
                    text = name,
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = subtitle,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.padding(top = AppTheme.spacing.xxs),
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                    modifier = Modifier.padding(top = AppTheme.spacing.sm),
                ) {
                    AppIcon(name = "location_on", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
                    Text(
                        text = city,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                }
            }
        }
    }
}
